// ---------------------------------------------------------------------------
// useExplorerUrlState
// ---------------------------------------------------------------------------
// Synchronises Explorer state (period, dataSource, view, trends, search,
// platforms, channels, dateFrom, dateTo) with URL query parameters via
// react-router-dom's useSearchParams.
//
// Defaults are omitted from the URL to keep it clean.
// Invalid values fall back to defaults.
// ---------------------------------------------------------------------------

import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import type {
  TimePeriod,
  DataSourceId,
  ViewMode,
  PlatformId,
  DiscoveryUrlState,
} from '@/components/v11/discovery/types'

import {
  DEFAULT_PERIOD,
  DEFAULT_DATA_SOURCE,
  DEFAULT_VIEW,
  VALID_PERIODS,
  VALID_DATA_SOURCES,
  VALID_PLATFORMS,
} from '@/components/v11/discovery/constants'

// ---------------------------------------------------------------------------
// Param key constants (URL query param names)
// ---------------------------------------------------------------------------

const PARAM_PERIOD = 'period'
const PARAM_DATE_FROM = 'dateFrom'
const PARAM_DATE_TO = 'dateTo'
const PARAM_SOURCE = 'source'
const PARAM_VIEW = 'view'
const PARAM_TRENDS = 'trends'
const PARAM_SEARCH = 'search'
const PARAM_PLATFORM = 'platform'
const PARAM_CHANNEL = 'channel'
const PARAM_DRILL_SOURCE = 'drillSource'
const PARAM_DRILL_ID = 'drillId'
const PARAM_DRILL_LABEL = 'drillLabel'

const EXPLORER_PARAMS = [
  PARAM_PERIOD,
  PARAM_DATE_FROM,
  PARAM_DATE_TO,
  PARAM_SOURCE,
  PARAM_VIEW,
  PARAM_TRENDS,
  PARAM_SEARCH,
  PARAM_PLATFORM,
  PARAM_CHANNEL,
  PARAM_DRILL_SOURCE,
  PARAM_DRILL_ID,
  PARAM_DRILL_LABEL,
] as const

const VALID_VIEWS: ReadonlyArray<ViewMode> = ['table', 'chart']

// YYYY-MM-DD format validation
const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/

// Demo mode - use dates from env vars
const isDemoMode = import.meta.env.VITE_SHOW_DEMO === 'true'
const DEMO_DATE_FROM = import.meta.env.VITE_DEMO_START_DATE || ''
const DEMO_DATE_TO = import.meta.env.VITE_DEMO_END_DATE || ''

// ---------------------------------------------------------------------------
// Validation helpers (pure functions)
// ---------------------------------------------------------------------------

function isValidPeriod(value: string): value is TimePeriod {
  return (VALID_PERIODS as ReadonlyArray<string>).includes(value)
}

function isValidDataSource(value: string): value is DataSourceId {
  return (VALID_DATA_SOURCES as ReadonlyArray<string>).includes(value)
}

function isValidView(value: string): value is ViewMode {
  return (VALID_VIEWS as ReadonlyArray<string>).includes(value)
}

function isValidPlatform(value: string): value is PlatformId {
  return (VALID_PLATFORMS as ReadonlyArray<string>).includes(value)
}

function isValidDate(value: string | null): boolean {
  if (!value) return false
  return DATE_REGEX.test(value) && !isNaN(Date.parse(value))
}

function parseCommaSeparated(raw: string | null): ReadonlyArray<string> {
  if (!raw) return []
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
}

function parsePlatforms(raw: string | null): ReadonlyArray<PlatformId> {
  const items = parseCommaSeparated(raw)
  const valid = items.filter(isValidPlatform)
  // Deduplicate
  return Array.from(new Set(valid))
}

// ---------------------------------------------------------------------------
// State reader (pure function)
// ---------------------------------------------------------------------------

function readState(searchParams: URLSearchParams): DiscoveryUrlState {
  const rawPeriod = searchParams.get(PARAM_PERIOD) ?? ''
  const rawDateFrom = searchParams.get(PARAM_DATE_FROM)
  const rawDateTo = searchParams.get(PARAM_DATE_TO)
  const rawSource = searchParams.get(PARAM_SOURCE) ?? ''
  const rawView = searchParams.get(PARAM_VIEW) ?? ''
  const rawTrends = searchParams.get(PARAM_TRENDS)
  const rawSearch = searchParams.get(PARAM_SEARCH) ?? ''
  const rawPlatform = searchParams.get(PARAM_PLATFORM)
  const rawChannel = searchParams.get(PARAM_CHANNEL)
  const rawDrillSource = searchParams.get(PARAM_DRILL_SOURCE)
  const rawDrillId = searchParams.get(PARAM_DRILL_ID)
  const rawDrillLabel = searchParams.get(PARAM_DRILL_LABEL)

  // In DEMO mode, default to custom dates if no period is explicitly set
  const hasExplicitPeriod = rawPeriod !== ''
  const hasDemoDates = isValidDate(DEMO_DATE_FROM) && isValidDate(DEMO_DATE_TO)
  const useDemoDates = isDemoMode && hasDemoDates && !hasExplicitPeriod

  return {
    period: useDemoDates ? 'custom' : (isValidPeriod(rawPeriod) ? rawPeriod : DEFAULT_PERIOD),
    dateFrom: useDemoDates ? DEMO_DATE_FROM : (isValidDate(rawDateFrom) ? rawDateFrom : null),
    dateTo: useDemoDates ? DEMO_DATE_TO : (isValidDate(rawDateTo) ? rawDateTo : null),
    dataSource: isValidDataSource(rawSource) ? rawSource : DEFAULT_DATA_SOURCE,
    view: isValidView(rawView) ? rawView : DEFAULT_VIEW,
    trends: rawTrends === 'on',
    search: rawSearch,
    platforms: parsePlatforms(rawPlatform),
    channels: parseCommaSeparated(rawChannel),
    drillSource: rawDrillSource && isValidDataSource(rawDrillSource) ? rawDrillSource : null,
    drillId: rawDrillId || null,
    drillLabel: rawDrillLabel || null,
  }
}

// ---------------------------------------------------------------------------
// Hook return type
// ---------------------------------------------------------------------------

/** Parameters for setting drill-down state */
export interface DrillDownUrlParams {
  readonly sourceDataSource: DataSourceId
  readonly targetDataSource: DataSourceId
  readonly filterId: string
  readonly filterLabel: string
}

export interface UseExplorerUrlStateReturn {
  readonly state: DiscoveryUrlState
  readonly setPeriod: (period: TimePeriod) => void
  readonly setCustomDates: (dateFrom: string, dateTo: string) => void
  readonly clearCustomDates: () => void
  readonly setDataSource: (source: DataSourceId, options?: { clearDrillDown?: boolean }) => void
  readonly setView: (view: ViewMode) => void
  readonly setTrends: (open: boolean) => void
  readonly setSearch: (search: string) => void
  readonly setPlatforms: (platforms: ReadonlyArray<PlatformId>) => void
  readonly setChannels: (channels: ReadonlyArray<string>) => void
  readonly setDrillDown: (params: DrillDownUrlParams) => void
  readonly clearDrillDown: () => void
  readonly resetAll: () => void
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useExplorerUrlState(): UseExplorerUrlStateReturn {
  const [searchParams, setSearchParams] = useSearchParams()

  const state = useMemo(() => readState(searchParams), [searchParams])

  // -----------------------------------------------------------------------
  // Updater helpers -- each creates a NEW URLSearchParams (immutable)
  // -----------------------------------------------------------------------

  const setPeriod = useCallback(
    (period: TimePeriod) => {
      if (!isValidPeriod(period)) return

      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        if (period === DEFAULT_PERIOD) {
          next.delete(PARAM_PERIOD)
        } else {
          next.set(PARAM_PERIOD, period)
        }
        // Clear custom dates when switching to non-custom period
        if (period !== 'custom') {
          next.delete(PARAM_DATE_FROM)
          next.delete(PARAM_DATE_TO)
        }
        return next
      })
    },
    [setSearchParams],
  )

  const setCustomDates = useCallback(
    (dateFrom: string, dateTo: string) => {
      if (!isValidDate(dateFrom) || !isValidDate(dateTo)) return

      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.set(PARAM_PERIOD, 'custom')
        next.set(PARAM_DATE_FROM, dateFrom)
        next.set(PARAM_DATE_TO, dateTo)
        return next
      })
    },
    [setSearchParams],
  )

  const clearCustomDates = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete(PARAM_DATE_FROM)
      next.delete(PARAM_DATE_TO)
      next.delete(PARAM_PERIOD) // revert to default period
      return next
    })
  }, [setSearchParams])

  const setDataSource = useCallback(
    (source: DataSourceId, options?: { clearDrillDown?: boolean }) => {
      if (!isValidDataSource(source)) return

      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        // Always set source explicitly (no special case for default)
        next.set(PARAM_SOURCE, source)
        // Optionally clear drill-down params in the same atomic operation
        if (options?.clearDrillDown) {
          next.delete(PARAM_DRILL_SOURCE)
          next.delete(PARAM_DRILL_ID)
          next.delete(PARAM_DRILL_LABEL)
        }
        return next
      })
    },
    [setSearchParams],
  )

  const setView = useCallback(
    (view: ViewMode) => {
      if (!isValidView(view)) return

      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        if (view === DEFAULT_VIEW) {
          next.delete(PARAM_VIEW)
        } else {
          next.set(PARAM_VIEW, view)
        }
        return next
      })
    },
    [setSearchParams],
  )

  const setTrends = useCallback(
    (open: boolean) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        if (open) {
          next.set(PARAM_TRENDS, 'on')
        } else {
          next.delete(PARAM_TRENDS)
        }
        return next
      })
    },
    [setSearchParams],
  )

  const setSearch = useCallback(
    (search: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        if (search.length === 0) {
          next.delete(PARAM_SEARCH)
        } else {
          next.set(PARAM_SEARCH, search)
        }
        return next
      })
    },
    [setSearchParams],
  )

  const setPlatforms = useCallback(
    (platforms: ReadonlyArray<PlatformId>) => {
      const valid = platforms.filter(isValidPlatform)

      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        if (valid.length === 0) {
          next.delete(PARAM_PLATFORM)
        } else {
          next.set(PARAM_PLATFORM, valid.join(','))
        }
        return next
      })
    },
    [setSearchParams],
  )

  const setChannels = useCallback(
    (channels: ReadonlyArray<string>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        if (channels.length === 0) {
          next.delete(PARAM_CHANNEL)
        } else {
          next.set(PARAM_CHANNEL, channels.join(','))
        }
        return next
      })
    },
    [setSearchParams],
  )

  const setDrillDown = useCallback(
    (params: DrillDownUrlParams) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        // Set drill-down params
        next.set(PARAM_DRILL_SOURCE, params.sourceDataSource)
        next.set(PARAM_DRILL_ID, params.filterId)
        next.set(PARAM_DRILL_LABEL, params.filterLabel)
        // Navigate to target data source
        next.set(PARAM_SOURCE, params.targetDataSource)
        return next
      })
    },
    [setSearchParams],
  )

  const clearDrillDown = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete(PARAM_DRILL_SOURCE)
      next.delete(PARAM_DRILL_ID)
      next.delete(PARAM_DRILL_LABEL)
      return next
    })
  }, [setSearchParams])

  const resetAll = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      for (const key of EXPLORER_PARAMS) {
        next.delete(key)
      }
      return next
    })
  }, [setSearchParams])

  return {
    state,
    setPeriod,
    setCustomDates,
    clearCustomDates,
    setDataSource,
    setView,
    setTrends,
    setSearch,
    setPlatforms,
    setChannels,
    setDrillDown,
    clearDrillDown,
    resetAll,
  }
}
